import type { AdminRole } from '@/lib/api-admin';
import type { OrderDetail } from '@/lib/api-admin/types';

/**
 * 무통장 입금확인 RBAC (M-4 ④, [ID52]). be-order 동결 계약:
 * `confirm-deposit` 의 @PreAuthorize 가 SUPER_ADMIN / OPS_ADMIN 만 허용한다고 가정
 * (구매확정·취소승인 등 운영 액션과 동일 게이트). 권위는 BE 가 최종 판정.
 */
// QA-487 — BE confirm-deposit·restore-pending 은 CS_AGENT 도 허용(@PreAuthorize OPS_ADMIN·CS_AGENT·SUPER_ADMIN).
//  BO 만 SUPER·OPS 로 좁혀 도매팀(CS) 화면에서 [입금확인]·[취소 복원]이 안 보였다 → BE 와 맞춘다.
export const CONFIRM_DEPOSIT_ROLES: ReadonlyArray<AdminRole> = [
  'SUPER_ADMIN',
  'OPS_ADMIN',
  'CS_AGENT',
];

export const canConfirmDeposit = (roles: ReadonlyArray<AdminRole>): boolean =>
  CONFIRM_DEPOSIT_ROLES.some((r) => roles.includes(r));

/**
 * 무통장(직접입금) 결제수단 식별값 — be-order 동결 = `BANK_TRANSFER`
 * (OrderDtos.PaymentMethod.BANK_TRANSFER). M-4 가 신설한 결제수단으로 generated
 * OpenAPI PaymentMethod enum 에 반영된다. VIRTUAL_ACCOUNT 는 PG 가상계좌(입금
 * 콜백 자동확정)라 수동 입금확인 대상이 아니다 — 자체 무통장(직접입금)에 한정(M-4 ① vs ②).
 */
const BANK_TRANSFER_METHOD = 'BANK_TRANSFER';

/**
 * 입금확인 버튼 노출 조건 — 입금대기(PENDING_PAYMENT) + 무통장(BANK_TRANSFER) 주문.
 * 그 외 상태/수단에서는 버튼 자체를 노출하지 않으며, 노출되더라도 그 외 상태는
 * BE 가 409 로 거부하고 friendlyMessage 가 사유를 안내한다.
 */
export const isBankTransferPendingOrder = (order: OrderDetail): boolean =>
  order.orderStatus === 'PENDING_PAYMENT' &&
  order.paymentMethod === BANK_TRANSFER_METHOD;

/**
 * QA-472 [취소 복원] 노출 — 취소(관리자취소·자동취소 등) + 무통장(직접입금) + 결제일시 없음(결제된 적 없음).
 * 가상계좌는 고도몰 발급 계좌라 복원 대상이 아니다. 쿠폰 사용·클레임 기록 주문은 BE 가 409 로 막는다.
 */
export const isRestorableCanceledBankTransferOrder = (order: OrderDetail): boolean =>
  order.orderStatus === 'CANCELED' && order.paymentMethod === BANK_TRANSFER_METHOD && !order.paidAt;
